import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Stethoscope } from "lucide-react";
import { doctorsApi, staffQueryKeys } from "../../shared/api/staff";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { LicenseBadge } from "./components/LicenseBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { HIGHLIGHT_ROW_CLASS, useHighlightRow } from "../../shared/hooks/useHighlightRow";

export function DoctorsSettingsPage() {
  const highlight = useHighlightRow(); // #19683 — Audit log trỏ tới một dòng
  const doctors = useQuery({
    queryKey: staffQueryKeys.doctors(),
    queryFn: () => doctorsApi.list({ size: 200, sort: "specialty" }),
  });

  const expiring = useQuery({
    queryKey: staffQueryKeys.expiringLicenses(60),
    queryFn: () => doctorsApi.expiringLicenses(60),
  });

  return (
    <div className="space-y-5 font-sans">
      <PageHeader
        title="Bác sĩ"
        description={
          <>
            Hồ sơ chuyên môn, chuyên khoa và giấy phép hành nghề của đội ngũ bác sĩ.
            Thêm bác sĩ bằng cách mời nhân viên mới và chọn vị trí <em>Bác sĩ</em>.
          </>
        }
        actions={
          <Button asChild>
            <Link to="/settings/users/new">+ Mời bác sĩ mới</Link>
          </Button>
        }
      />

      {expiring.data && expiring.data.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Có {expiring.data.length} giấy phép sắp hết hạn trong 60 ngày tới —
            xem danh sách bên dưới và liên hệ Sở Y tế để gia hạn kịp thời.
          </span>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-4">
          <CardTitle>Danh sách bác sĩ</CardTitle>
          {(doctors.data?.content.length ?? 0) > 0 && (
            <span className="text-sm text-muted-foreground">
              {doctors.data?.content.length} bác sĩ
            </span>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {doctors.isLoading && (
            <p className="px-6 pb-6 text-sm text-muted-foreground">Đang tải…</p>
          )}
          {doctors.isError && (
            <div className="mx-6 mb-6 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {apiError(doctors.error).detail}
            </div>
          )}
          {!doctors.isLoading && (doctors.data?.content.length ?? 0) === 0 && (
            <div className="px-5 py-10 text-center text-muted-foreground">
              <Stethoscope className="mx-auto mb-3 h-9 w-9" aria-hidden="true" />
              <div className="mb-1.5 font-semibold text-foreground">
                Chưa có bác sĩ nào
              </div>
              <div className="mb-5 text-sm">
                Mời bác sĩ đầu tiên để họ bắt đầu tiếp nhận lịch hẹn và kê đơn thuốc.
              </div>
              <Button asChild>
                <Link to="/settings/users/new">+ Mời bác sĩ mới</Link>
              </Button>
            </div>
          )}
          {(doctors.data?.content.length ?? 0) > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bác sĩ</TableHead>
                  <TableHead>Học vị</TableHead>
                  <TableHead>Chuyên khoa</TableHead>
                  <TableHead className="text-center">Kê đơn</TableHead>
                  <TableHead>Chữ ký</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {doctors.data?.content.map((d) => (
                  <TableRow
                    key={d.id}
                    ref={highlight.isHighlighted(d.id) ? highlight.scrollRef : undefined}
                    className={`cursor-pointer ${highlight.isHighlighted(d.id) ? HIGHLIGHT_ROW_CLASS : ""}`}
                    onClick={() =>
                      window.location.assign(`/settings/users/${d.staffProfileId}`)
                    }
                  >
                    <TableCell>
                      <span className="font-semibold text-foreground">
                        {d.fullName ?? "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {d.title ?? "—"}
                    </TableCell>
                    <TableCell>{d.specialty ?? "—"}</TableCell>
                    <TableCell className="text-center">
                      {d.prescriber ? (
                        <span
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"
                          aria-hidden="true"
                        >
                          <Check className="h-3 w-3" />
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <SignatureCell doctorId={d.id} />
                    </TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="sm">
                        <Link
                          to={`/settings/users/${d.staffProfileId}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Mở hồ sơ →
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-4">
          <CardTitle>Giấy phép sắp hết hạn</CardTitle>
          <span className="text-sm text-muted-foreground">60 ngày tới</span>
        </CardHeader>
        <CardContent>
          {expiring.isLoading && (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          )}
          {expiring.data && expiring.data.length === 0 && (
            <div className="flex items-center gap-2.5 py-1 text-sm text-emerald-700">
              <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Tất cả giấy phép hành nghề còn hiệu lực — không có gì cần gia hạn.
              </span>
            </div>
          )}
          {(expiring.data?.length ?? 0) > 0 && (
            <div className="divide-y divide-border">
              {expiring.data?.map((l) => (
                <div
                  key={l.id}
                  className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                      {l.licenseNumber}{" "}
                      <LicenseBadge validUntil={l.validUntil} expired={l.expired} />
                    </div>
                    <div className="mt-0.5 text-sm text-muted-foreground">
                      {l.issuedBy ?? "—"}
                      {l.notes && ` · ${l.notes}`}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <Button asChild variant="secondary" size="sm">
                      <Link to={`/settings/users?doctor=${l.doctorProfileId}`}>
                        Mở hồ sơ
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Per-doctor signature: preview + upload (printed on prescriptions). */
function SignatureCell({ doctorId }: { doctorId: string }) {
  const qc = useQueryClient();
  const key = ["doctor-signature", doctorId] as const;
  const q = useQuery({ queryKey: key, queryFn: () => doctorsApi.signatureUrl(doctorId) });
  const inputRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) => doctorsApi.uploadSignature(doctorId, file),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => setErr(apiError(e).detail),
  });
  return (
    <div className="flex items-center gap-2">
      {q.data ? (
        <img
          src={q.data}
          alt="Chữ ký"
          className="h-8 max-w-[96px] rounded border bg-white object-contain"
        />
      ) : (
        <span className="text-xs text-muted-foreground">Chưa có</span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={upload.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {upload.isPending ? "Đang tải…" : q.data ? "Đổi" : "Tải lên"}
      </Button>
      {err && <span className="text-xs text-destructive">{err}</span>}
    </div>
  );
}
